import { lazy, type LazyExoticComponent, type ComponentType } from 'react';
import type { AppDefinition, RouteDefinition, DashboardApp } from './types';

/**
 * Central App Registry
 * =====================
 * 
 * SINGLE SOURCE OF TRUTH for all apps in the platform.
 * 
 * To add a new app:
 * 1. Add entry to APP_REGISTRY below
 * 2. Create the app package in packages/app-{name}/
 * 3. Done! Routes and Dashboard will auto-update.
 */
export const APP_REGISTRY: AppDefinition[] = [
  // ============================================
  // CREATIVE APPS
  // ============================================
  {
    id: 'design-search',
    name: 'Design Search',
    description: 'Search designs from the archive',
    icon: '🎨',
    path: '/apps/design-search',
    color: '#ec4899',
    category: 'Creative',
    status: 'live',
    lucideIcon: 'Palette',
    gradient: 'from-pink-500 to-rose-500',
    component: () => import('./lazy/DesignSearch'),
  },

  // ============================================
  // MANAGEMENT APPS
  // ============================================
  {
    id: 'kpi',
    name: 'KPI Dashboard',
    description: 'View team KPI statistics and leaderboard',
    icon: '📊',
    path: '/apps/kpi',
    color: '#f59e0b',
    category: 'Management',
    status: 'live',
    lucideIcon: 'BarChart3',
    gradient: 'from-emerald-500 to-teal-500',
    component: () => import('@bapecom/app-kpi').then(m => ({ default: m.KPIDashboard })),
  },
  {
    id: 'team-kpi',
    name: 'Team KPI Tracker',
    description: 'Monitor KPI progress for all designers',
    icon: '👥',
    path: '/apps/team-kpi',
    color: '#8b5cf6',
    category: 'Management',
    status: 'live',
    lucideIcon: 'Users',
    gradient: 'from-violet-500 to-purple-500',
    component: () => import('@bapecom/app-kpi').then(m => ({ default: m.TeamKPITracker })),
  },
  {
    id: 'hr',
    name: 'HR Management',
    description: 'Manage employees and personnel information',
    icon: '👥',
    path: '/apps/hr',
    color: '#10b981',
    category: 'Management',
    status: 'development',
    lucideIcon: 'Users',
    gradient: 'from-purple-500 to-violet-500',
    component: () => import('./lazy/ComingSoon'),
  },

  // ============================================
  // UTILITY APPS
  // ============================================
  {
    id: 'syno-files',
    name: 'Syno Files',
    description: 'Access and manage files on Synology NAS',
    icon: '💾',
    path: '/apps/syno-files',
    color: '#8b5cf6',
    category: 'Utility',
    status: 'live',
    lucideIcon: 'HardDrive',
    gradient: 'from-blue-500 to-indigo-500',
    component: () => import('./lazy/SynoSearch'),
  },
  {
    id: 'orders',
    name: 'Orders Manager',
    description: 'Manage and track all orders',
    icon: '🛒',
    path: '/apps/orders',
    color: '#f97316',
    category: 'Utility',
    status: 'coming_soon',
    lucideIcon: 'ShoppingBag',
    gradient: 'from-orange-500 to-red-500',
    component: () => import('./lazy/ComingSoon'),
  },

  // ============================================
  // PERSONAL APPS
  // ============================================
  {
    id: 'kpi-tracker',
    name: 'My KPI Tracker',
    description: 'Track your personal KPI progress and targets',
    icon: '🎯',
    path: '/apps/kpi-tracker',
    color: '#06b6d4',
    category: 'Personal',
    status: 'live',
    lucideIcon: 'Target',
    gradient: 'from-cyan-500 to-blue-500',
    permissionId: 'kpi', // Shares permission with KPI Dashboard
    component: () => import('@bapecom/app-kpi').then(m => ({ default: m.KPITracker })),
  },

  // ============================================
  // ADMIN APPS
  // ============================================
  {
    id: 'admin',
    name: 'Admin Panel',
    description: 'System administration and user management',
    icon: '⚙️',
    path: '/apps/admin',
    color: '#64748b',
    category: 'Admin',
    status: 'live',
    lucideIcon: 'Settings',
    gradient: 'from-slate-500 to-slate-700',
    requireAdmin: true,
    component: () => import('./lazy/AdminPanel'),
  },
];

/**
 * Get all registered apps
 */
export const getApps = (): AppDefinition[] => APP_REGISTRY;

/**
 * Get app by ID
 */
export const getAppById = (id: string): AppDefinition | undefined => 
  APP_REGISTRY.find(app => app.id === id);

/**
 * Get apps by category
 */
export const getAppsByCategory = (category: AppDefinition['category']): AppDefinition[] =>
  APP_REGISTRY.filter(app => app.category === category);

/**
 * Get apps by status
 */
export const getAppsByStatus = (status: AppDefinition['status']): AppDefinition[] =>
  APP_REGISTRY.filter(app => app.status === status);

/**
 * Get live apps only
 */
export const getLiveApps = (): AppDefinition[] =>
  APP_REGISTRY.filter(app => app.status === 'live');

/**
 * Generate route definitions for React Router
 */
export const generateRoutes = (): RouteDefinition[] => {
  const routes: RouteDefinition[] = [];

  for (const app of APP_REGISTRY) {
    // Main route
    routes.push({
      path: app.path,
      appId: app.id,
      permissionId: app.permissionId || app.id,
      requireAdmin: app.requireAdmin || false,
      component: lazy(app.component) as LazyExoticComponent<ComponentType<unknown>>,
    });

    // Sub routes
    if (app.subRoutes) {
      for (const subRoute of app.subRoutes) {
        routes.push({
          path: subRoute.path,
          appId: app.id,
          permissionId: subRoute.permissionId || app.permissionId || app.id,
          requireAdmin: app.requireAdmin || false,
          component: lazy(subRoute.component) as LazyExoticComponent<ComponentType<unknown>>,
        });
      }
    }
  }

  return routes;
};

/**
 * Convert registry to dashboard-friendly format
 */
export const getDashboardApps = (): DashboardApp[] => {
  return APP_REGISTRY.map(app => ({
    id: app.id,
    name: app.name,
    description: app.description,
    icon: app.icon,
    url: app.path,
    color: app.color,
    category: app.category,
    status: app.status,
    lucideIcon: app.lucideIcon,
    gradient: app.gradient,
  }));
};
